import { Breakpoint, useVisibleColumnCount } from "./useVisibleColumns";

/**
 * One named source of truth for a table's responsive columns.
 *
 * WHY THIS EXISTS
 * Hiding columns below a breakpoint needs two edits that must agree exactly:
 * the `<th>` and its `<td>`. I got that wrong twice in one afternoon — on
 * Tickets I hid the Priority and Assignee HEADERS but not their cells, so
 * every later column sat one place left of its heading and the rows showed
 * values under the wrong titles. It looked plausible on screen; only a script
 * that counted visible `<th>` against visible `<td>` caught it.
 *
 * A per-column class written once and applied to both ends makes that class
 * of bug structurally impossible, and `spanAll` gives the `colSpan` for
 * full-width rows (empty states, group headers, expansion rows) — an
 * attribute no media query can reach, which is the other trap.
 *
 * The remaining 31 tables to convert are listed by measured overflow in
 * `C:\Temp\shot\tablesweep.js`.
 *
 * USAGE
 *   const cols = useResponsiveColumns({
 *     name:     "base",   // always on screen
 *     customer: "sm",     // appears at >=640px
 *     ip:       "md",     // >=768
 *     agent:    "lg",     // >=1024
 *   });
 *
 *   <th className={cols.cls("customer")}>Customer</th>
 *   ...
 *   <td className={`px-4 py-2 ${cols.cls("customer")}`}>{row.customer}</td>
 *   ...
 *   <td colSpan={cols.spanAll}>No rows</td>
 *
 * Keys are deliberately names, not indices: a table gains and loses columns
 * during a refactor, and an index-keyed spec silently re-points every
 * breakpoint after the one that moved.
 */

/** The class that hides a column until its breakpoint. */
const HIDE: Record<Breakpoint, string> = {
  base: "",
  sm: "hidden sm:table-cell",
  md: "hidden md:table-cell",
  lg: "hidden lg:table-cell",
  xl: "hidden xl:table-cell",
};

export type ColumnSpec<K extends string> = Record<K, Breakpoint>;

export type ResponsiveColumns<K extends string> = {
  /** Class for both the `<th>` and the `<td>` of that column. */
  cls: (key: K) => string;
  /** Columns on screen right now — the colSpan for a full-width row. */
  spanAll: number;
  /** Is that column currently on screen? For a mobile-only sub-line. */
  shown: (key: K) => boolean;
};

export function useResponsiveColumns<K extends string>(
  spec: ColumnSpec<K>,
  /**
   * Columns the table renders conditionally (a "Customer" column only shown
   * in all-customers mode). Listed here so `spanAll` stays right instead of
   * over-spanning and re-inflating the table.
   */
  omit: K[] = [],
): ResponsiveColumns<K> {
  const keys = (Object.keys(spec) as K[]).filter((k) => !omit.includes(k));
  const spanAll = useVisibleColumnCount(keys.map((k) => spec[k]));

  const cls = (key: K): string => {
    const bp = spec[key];
    if (bp === undefined) {
      // A typo'd key would otherwise return undefined and read as "always
      // visible" — the exact silent mismatch this hook exists to prevent.
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.error(
          `useResponsiveColumns: no column "${key}" in spec (have: ${Object.keys(spec).join(", ")})`,
        );
      }
      return "";
    }
    return HIDE[bp];
  };

  const shownAt = (bp: Breakpoint) =>
    typeof window === "undefined"
      ? true
      : window.innerWidth >= { base: 0, sm: 640, md: 768, lg: 1024, xl: 1280 }[bp];

  return { cls, spanAll, shown: (key: K) => shownAt(spec[key] ?? "base") };
}
